import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, BadgeCheck, Building, MapPin, Phone as PhoneIcon, Mail, IdCard,
  Percent, IndianRupee, TrendingUp, Users, CheckCircle, Clock, StickyNote,
  KeyRound, ShieldCheck,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { cn } from '../../utils/tailwindUtils';
import {
  getEngageStatusBadge, ENGAGE_STATUS_LABEL,
} from '../../utils/engageStageBadge';
import PartnerCredentialsModal from './PartnerCredentialsModal';
import ToastMsg from '../../components/common/ToastMsg';


const REFERRAL_STATUS_LABEL = {
  referred: 'Referred',
  contacted: 'Contacted',
  appointment_booked: 'Appointment Booked',
  converted: 'Converted',
  dropped: 'Dropped',
};

const REFERRAL_STATUS_BADGE = {
  referred: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  contacted: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  appointment_booked: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  converted: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  dropped: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

function formatINR(n) {
  return Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}


export default function ReferringDoctorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetch: apiFetch, create: apiCreate } = useApiMethods({ throwOnFail: true });

  const [doctor, setDoctor] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [credsModal, setCredsModal] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState(null);
  const [marking, setMarking] = useState({});

  const reload = async () => {
    setLoading(true);
    try {
      const [dResp, rResp] = await Promise.all([
        apiFetch(`engage/referring-doctors/${id}/`),
        apiFetch(`engage/referrals/?referring_doctor=${id}`),
      ]);
      setDoctor(dResp?.data || dResp?.data?.data || null);
      const rows = rResp?.results || rResp?.data?.results || [];
      setReferrals(Array.isArray(rows) ? rows : []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load doctor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const markPaid = async (refId) => {
    if (marking[refId]) return;
    setMarking(m => ({ ...m, [refId]: true }));
    try {
      await apiCreate(`/engage/referrals/${refId}/mark-paid/`, {});
      await reload();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to mark paid');
    } finally {
      setMarking(m => ({ ...m, [refId]: false }));
    }
  };

  const stats = useMemo(() => {
    const referred = referrals.length;
    const converted = referrals.filter(r => r.status === 'converted').length;
    const unpaid = referrals.filter(r => !r.incentive_paid)
      .reduce((s, r) => s + Number(r.incentive_amount || 0), 0);
    const paid = referrals.filter(r => r.incentive_paid)
      .reduce((s, r) => s + Number(r.incentive_amount || 0), 0);
    return { referred, converted, unpaid, paid };
  }, [referrals]);

  if (loading && !doctor) {
    return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading doctor…</div>;
  }
  if (!doctor) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
          {error || 'Doctor not found.'}
        </div>
        <button
          onClick={() => navigate('/engage/referring-doctors')}
          className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      {/* Back + Edit */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/engage/referring-doctors')}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Referring Doctors
        </button>
        <button
          onClick={() => navigate(`/engage/referring-doctors/${id}/edit`)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Edit
        </button>
      </div>

      {/* Header card */}
      <div className="bg-gradient-to-r from-purple-500/5 to-indigo-500/5 dark:from-purple-500/10 dark:to-indigo-500/10 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold shadow-sm">
              {(doctor.name || '?').replace(/^Dr\.?\s*/i, '').charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">{doctor.name}</h1>
              <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                {doctor.specialty && <span>{doctor.specialty}</span>}
                {doctor.clinic_or_hospital && <span>• {doctor.clinic_or_hospital}</span>}
                {doctor.is_verified && (
                  <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded-full">
                    <BadgeCheck className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Portal login card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Partner portal login</div>
            {doctor.portal_username ? (
              <div className="flex items-center gap-2 mt-0.5">
                <code className="text-sm font-mono text-gray-900 dark:text-white">{doctor.portal_username}</code>
                {doctor.portal_login_active === false && (
                  <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                    Inactive
                  </span>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-500 mt-0.5 italic">Not provisioned yet</div>
            )}
          </div>
        </div>
        <button
          onClick={async () => {
            if (!window.confirm(doctor.portal_username
              ? `Reset password for ${doctor.name}? The new password will be shown once — copy it before closing.`
              : `Create a portal login for ${doctor.name}?`)) return;
            setResetting(true);
            try {
              const resp = await apiCreate(`/engage/referring-doctors/${id}/reset-login/`, {});
              const creds = resp?.data || resp?.data?.data;
              if (creds?.password) {
                setCredsModal({ creds, name: doctor.name });
              } else {
                ToastMsg('success', 'Login updated');
              }
            } catch (e) {
              ToastMsg('error', e?.response?.data?.message || 'Failed to reset login');
            } finally {
              setResetting(false);
            }
          }}
          disabled={resetting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/60 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 disabled:opacity-50"
        >
          <KeyRound className="w-3.5 h-3.5" />
          {resetting ? 'Working…' : (doctor.portal_username ? 'Reset password' : 'Create login')}
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Referred</span>
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">{stats.referred}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Converted</span>
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">
            {stats.converted}
            <span className="text-xs text-gray-500 ml-1">
              ({stats.referred ? Math.round(stats.converted / stats.referred * 100) : 0}%)
            </span>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-amber-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Owed</span>
          </div>
          <div className="text-xl font-bold mt-1 text-amber-600 dark:text-amber-400">₹ {formatINR(stats.unpaid)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Paid</span>
          </div>
          <div className="text-xl font-bold mt-1 text-green-600 dark:text-green-400">₹ {formatINR(stats.paid)}</div>
        </div>
      </div>

      {/* Two column: profile + referrals list */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Profile */}
        <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
          <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Doctor profile</h3>
          <div className="space-y-1.5 text-sm">
            {doctor.registration_number && (
              <div className="flex items-center gap-2"><IdCard className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">Reg: {doctor.registration_number}</span>
              </div>
            )}
            {doctor.mobile && (
              <div className="flex items-center gap-2"><PhoneIcon className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">{doctor.mobile}</span>
              </div>
            )}
            {doctor.alt_mobile && (
              <div className="flex items-center gap-2"><PhoneIcon className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">{doctor.alt_mobile} (alt)</span>
              </div>
            )}
            {doctor.email && (
              <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">{doctor.email}</span>
              </div>
            )}
            {doctor.city && (
              <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">{doctor.city}</span>
              </div>
            )}
            {doctor.clinic_or_hospital && (
              <div className="flex items-center gap-2"><Building className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">{doctor.clinic_or_hospital}</span>
              </div>
            )}
            <div className="flex items-center gap-2"><Percent className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-700 dark:text-gray-300">Commission: {doctor.referral_incentive_pct || 0}%</span>
            </div>
          </div>

          {doctor.notes && (
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                <StickyNote className="w-3 h-3" /> Notes
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{doctor.notes}</p>
            </div>
          )}
        </div>

        {/* Incentive ledger / referrals list */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Incentive ledger &amp; referred patients</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <th className="px-3 py-2 font-medium">Patient</th>
                  <th className="px-3 py-2 font-medium">Referred on</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium text-right">Incentive</th>
                  <th className="px-3 py-2 font-medium">Payment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {referrals.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-500 dark:text-gray-400">No referrals yet.</td></tr>
                ) : referrals.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-3 py-2.5">
                      {r.enquiry ? (
                        <Link to={`/engage/enquiries/${r.enquiry}`} className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
                          {r.patient_name || `Enquiry #${r.enquiry}`}
                        </Link>
                      ) : (
                        <span className="text-gray-500">{r.patient_name || '—'}</span>
                      )}
                      {r.clinical_reason && (
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-[280px]">
                          {r.clinical_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-[12px] text-gray-600 dark:text-gray-300">{fmtDate(r.referral_date)}</td>
                    <td className="px-3 py-2.5">
                      <span className={cn(
                        'inline-block px-2 py-0.5 rounded-full text-[10px] font-medium',
                        REFERRAL_STATUS_BADGE[r.status] || 'bg-gray-100 text-gray-600'
                      )}>
                        {REFERRAL_STATUS_LABEL[r.status] || r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-medium text-gray-900 dark:text-white">
                      ₹ {formatINR(r.incentive_amount)}
                    </td>
                    <td className="px-3 py-2.5">
                      {r.incentive_paid ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                          <CheckCircle className="w-3 h-3" /> Paid {r.incentive_paid_at ? `· ${fmtDate(r.incentive_paid_at)}` : ''}
                        </span>
                      ) : Number(r.incentive_amount) > 0 && r.status === 'converted' ? (
                        <button
                          onClick={() => markPaid(r.id)}
                          disabled={marking[r.id]}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 dark:text-amber-300 disabled:opacity-50"
                        >
                          {marking[r.id] ? 'Marking…' : 'Mark paid'}
                        </button>
                      ) : (
                        <span className="text-[11px] text-gray-400 inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Pending
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {credsModal && (
        <PartnerCredentialsModal
          partnerName={credsModal.name}
          partnerKind="doctor"
          credentials={credsModal.creds}
          onClose={() => { setCredsModal(null); reload(); }}
        />
      )}
    </div>
  );
}
